import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { ILanguage, ISystemPreferences, IUser, IUserSession } from 'src/app/core/dto';
import { ClientDataStore } from 'src/app/main/shared/services/clientData.store';
import { putAction } from 'src/app/main/shared/services/clientData.actions';
import { SettingsService } from 'src/app/main/shared/services/settings.service';
import { UnitsService } from 'src/app/main/shared/services/units.service';
import { selectUserSession, selectUsers } from 'src/app/main/shared/services/store/user/selector';
import { userPreferencesUpdated } from 'src/app/main/shared/services/store/userSession/actions';

const USE_SYSTEM_DEFAULT = '';

/**
 * Language and display-unit selectors for a single user.
 *
 * Used both on the profile page — where an administrator may be editing
 * somebody else, so `username` is supplied — and in the toolbar dropdown,
 * where it always edits the signed-in user and `username` is left off.
 *
 * With no `username` the current values come from the active session, which
 * already carries them; that keeps the toolbar from pulling the whole Users
 * collection on every page just to render two selects.
 */
@Component({
  selector: 'app-preferences-picker',
  templateUrl: './preferences-picker.component.html',
  styleUrls: ['./preferences-picker.component.scss'],
  imports: [FormsModule, MatFormFieldModule, MatSelectModule, TranslateModule],
})
export class PreferencesPickerComponent implements OnInit, OnDestroy {
  /** Whose preferences to edit. Omit to edit the signed-in user. */
  @Input() username?: string;
  /** Tighter layout for the toolbar dropdown; drops the system-default hints. */
  @Input() compact = false;

  readonly USE_SYSTEM_DEFAULT = USE_SYSTEM_DEFAULT;
  languages: ILanguage[] = [];
  unitOptions: { key: string; title: string }[] = [];
  systemPreferences?: ISystemPreferences;
  // Empty string = "use system default", otherwise the override key.
  selectedLanguageCode: string = USE_SYSTEM_DEFAULT;
  selectedInchesUnit: string = USE_SYSTEM_DEFAULT;

  private user?: IUser;
  private currentUserName: string | null = null;
  private subscriptions_: Subscription[] = [];

  constructor(
    private clientDataStore: ClientDataStore,
    private store: Store,
    private settingsService: SettingsService,
    private unitsService: UnitsService
  ) {
    this.unitOptions = this.unitsService.getBaseUnits();
  }

  ngOnInit(): void {
    this.subscriptions_.push(
      this.clientDataStore.SelectSystemPreferences().subscribe((prefs) => {
        this.systemPreferences = prefs;
      }),
      this.settingsService.getLanguages().subscribe((langs) => {
        this.languages = langs;
      }),
      this.store.select(selectUserSession).subscribe((session: IUserSession | null) => {
        this.currentUserName = session?.userName ?? null;
        if (!this.username) {
          this.selectedLanguageCode = session?.userLanguage ?? USE_SYSTEM_DEFAULT;
          this.selectedInchesUnit = session?.userInchesUnit ?? USE_SYSTEM_DEFAULT;
        }
      })
    );

    if (!this.username) {
      return;
    }

    this.subscriptions_.push(
      this.clientDataStore.SelectUsers().subscribe(),
      this.store
        .select(selectUsers)
        .pipe(map((users) => users.find((u) => u.userName === this.username)))
        .subscribe((user) => {
          if (!user) return;
          this.user = user;
          this.selectedLanguageCode = user.language ?? USE_SYSTEM_DEFAULT;
          this.selectedInchesUnit = user.inchesUnit ?? USE_SYSTEM_DEFAULT;
        })
    );
  }

  get isSelf(): boolean {
    if (!this.username) {
      return true;
    }
    return !!this.currentUserName && this.currentUserName === this.username;
  }

  get systemLanguageLabel(): string {
    const code = this.systemPreferences?.systemLanguage;
    if (!code) return '';
    return this.languages.find((l) => l.code === code)?.title ?? code;
  }

  get systemInchesUnitLabel(): string {
    const key = this.systemPreferences?.inchesUnit;
    if (!key) return '';
    return this.unitOptions.find((u) => u.key === key)?.title ?? key;
  }

  onChangeLanguage(code: string) {
    this.selectedLanguageCode = code;
    this.savePreferences({ language: code });
  }

  onChangeInchesUnit(key: string) {
    this.selectedInchesUnit = key;
    this.savePreferences({ inchesUnit: key });
  }

  private savePreferences(prefs: { language?: string; inchesUnit?: string }) {
    const obs = this.isSelf
      ? this.settingsService.saveSelfPreferences(prefs)
      : this.settingsService.saveUserPreferences(this.user!.userName, prefs);
    this.subscriptions_.push(
      obs.subscribe((res) => {
        // Admin path returns the updated user; refresh the local store so the
        // editor and any other consumers reflect the change immediately.
        if (res && (res as IUser).userName) {
          this.store.dispatch(putAction({ collection: 'Users', payload: res as IUser }));
        }
        // For self-edit, patch the active user session so UserPreferencesService
        // re-emits and the new language/units take effect without a reload.
        if (this.isSelf) {
          this.store.dispatch(
            userPreferencesUpdated({
              userLanguage: prefs.language,
              userInchesUnit: prefs.inchesUnit,
            })
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions_.forEach((sub) => sub.unsubscribe());
  }
}
